/**
 * SlicesPanel - Slices and slats panel
 * Matches the Java SketchChair slices panel.
 *
 * Standard properties-list layout inside the right panel:
 * - Vertical scrollable list of slice-group rows (type icon, "Ribs
 *   (11 slices)" label, delete x). Click selects the group.
 * - Options area below the list, visible ONLY while a group is selected:
 *   cap style row, option toggles (Through All / Flip / Smooth / Flush),
 *   Start/End/Spacing inputs, and a compact tools row (Single Slice,
 *   Slat, Slat Group, Edit). When nothing is selected a dim hint shows
 *   instead.
 *
 * The "+" button in the section header (UIManager) arms the interactive
 * slice-group placement tool (startSliceGroup); its armed state is
 * mirrored on that button via setHeaderAddButton.
 */

import * as THREE from 'three';
import { CrossSliceSelection } from '../../geometry/CrossSliceSelection.js';
import { SliceType } from '../../geometry/SliceType.js';
import { CapType } from '../../geometry/CapType.js';
import { SketchSpline } from '../../sketch/SketchSpline.js';
import { ICONS, phIcon } from '../icons.js';

export class SlicesPanel {
    constructor(application) {
        this.app = application;

        // Current slice tool state
        this.activeTool = null;
        this.activeCapStyle = CapType.CAP_CURVE;
        this.selectedSliceGroup = null; // Currently selected slice group

        // Slice options
        this.options = {
            throughAll: false,
            flipSlots: false,
            smooth: false,
            flushTops: false,
            curvedCaps: true // endcaps follow the rib curvature (default ON)
        };

        // 3-click workflow state
        this.editingSelection = null;
        this.guidePath = null;

        // Slice groups list container (will be populated dynamically)
        this.sliceGroupsList = null;

        // Buttons that arm the interactive slice tool, by label
        // (used to show the armed/active state)
        this.sliceToolButtons = {};

        this.createPanel();
    }

    /**
     * Create the panel DOM structure (vertical properties layout)
     */
    createPanel() {
        const panel = document.createElement('div');
        panel.className = 'slices-panel panel';
        panel.style.cssText = `
            display: flex;
            flex-direction: column;
            width: 100%;
        `;

        // Vertical scrollable slice-group list
        this.sliceGroupsList = document.createElement('div');
        this.sliceGroupsList.className = 'props-list slice-groups-list';
        panel.appendChild(this.sliceGroupsList);

        // Hint shown while no group is selected
        this.hintEl = document.createElement('div');
        this.hintEl.className = 'props-hint';
        this.hintEl.textContent = 'Select a slice group to edit, or + to add.';
        panel.appendChild(this.hintEl);

        // Options area (visible only while a group is selected)
        this.optionsArea = this.createOptionsArea();
        panel.appendChild(this.optionsArea);

        this.panel = panel;

        // Initial population (list, controls, visibility, active states)
        this.update();
    }

    /**
     * Register the section-header "+" button (owned by UIManager) so the
     * armed state of the interactive slice-group tool can be shown on it
     */
    setHeaderAddButton(button) {
        this.headerAddButton = button;
        this.updateSliceToolButtons();
    }

    /**
     * Options area under the group list: cap style, option toggles,
     * Start/End/Spacing inputs and the compact tools row
     */
    createOptionsArea() {
        const area = document.createElement('div');
        area.className = 'props-options';

        const makeLabel = (text) => {
            const el = document.createElement('div');
            el.className = 'props-field-label';
            el.textContent = text;
            return el;
        };
        const makeRow = () => {
            const row = document.createElement('div');
            row.className = 'props-options-row';
            return row;
        };

        // --- Cap style row ---
        area.appendChild(makeLabel('Cap style'));
        const capRow = makeRow();
        this.capButtons = [];
        [
            { capType: CapType.CAP_ROUND_SQUARE, icon: 'GUI_SLICE_CAP_ROUNDRECT.png', tooltip: 'Rounded - rounded rectangle cap' },
            { capType: CapType.CAP_BUTT, icon: 'GUI_SLICE_CAP_SQUARE.png', tooltip: 'Square - square cap' },
            { capType: CapType.CAP_CURVE, icon: 'GUI_SLICE_CAP_WING.png', tooltip: 'Winged - winged cap' },
            { capType: CapType.CAP_INSIDE, icon: 'GUI_SLICE_CUTOFF.png', tooltip: 'Cut-Off - flush with last layer' }
        ].forEach(cap => {
            const button = this.createOptionIconButton({
                icon: cap.icon,
                tooltip: cap.tooltip,
                onClick: () => this.setCapStyle(cap.capType)
            });
            this.capButtons.push({ button, capType: cap.capType });
            capRow.appendChild(button);
        });
        area.appendChild(capRow);

        // --- Option toggles row ---
        area.appendChild(makeLabel('Options'));
        const optionsRow = makeRow();
        this.optionToggleButtons = [];
        [
            { key: 'throughAll', icon: 'GUI_MATERIAL_THROUGHALL_SLOT.png', tooltip: 'Through All - cut through all shapes' },
            { key: 'flipSlots', icon: 'GUI_SLOT_ROTATE_DOWN.png', tooltip: 'Flip Slots - flip slot direction' },
            { key: 'smooth', icon: 'GUI_SLICE_SMOOTHED.png', tooltip: 'Smooth - form slices with curves' },
            { key: 'flushTops', icon: 'GUI_SLICE_FOLLOW_EDGE.png', tooltip: 'Flush Tops - add flat facets' },
            { key: 'curvedCaps', icon: 'cap_round.png', tooltip: 'Curved Caps - endcaps continue the rib curvature' }
        ].forEach(opt => {
            const button = this.createOptionIconButton({
                icon: opt.icon,
                tooltip: opt.tooltip,
                onClick: () => this.toggleOption(opt.key)
            });
            this.optionToggleButtons.push({ button, key: opt.key });
            optionsRow.appendChild(button);
        });
        area.appendChild(optionsRow);

        // Per-group numeric controls: Start / End (0..1) and Spacing (mm).
        // Teeth-count / finger-tolerance controls are intentionally NOT
        // added - finger joints aren't implemented yet.
        area.appendChild(this.createGroupControlsRow());

        // Leg-brace-only controls: crossbrace toggle + width/offset
        area.appendChild(this.createLegBraceControlsRow());

        // (The old tools row is gone: add types live in the header "+"
        // menu; Edit lives on each group row, shown on hover.)

        return area;
    }

    /**
     * Small icon button for the options area (legacy bitmap icon; the
     * active/toggle state is shown via the .active class)
     */
    createOptionIconButton({ icon, tooltip, onClick }) {
        const button = document.createElement('button');
        button.className = 'props-icon-btn';
        button.title = tooltip;

        const img = document.createElement('img');
        img.src = `/images/gui/${icon}`;
        img.alt = tooltip;
        button.appendChild(img);

        button.addEventListener('click', onClick);
        return button;
    }

    /**
     * Apply a live numeric edit to the selected slice group. Throttled
     * regenerations while dragging/typing; a final unthrottled pass on
     * commit. ONE undo snapshot per continuous edit burst.
     * @param {Function} apply - (selection, value) => void
     * @param {number} v
     * @param {boolean} throttled - true while mid-gesture
     */
    _applyLive(apply, v, throttled) {
        const sel = this.selectedSliceGroup;
        if (!sel || !Number.isFinite(v)) return;
        if (!this._liveUndoPushed) {
            this._liveUndoPushed = true;
            this.app.pushUndoSnapshot?.();
        }
        apply(sel, v);
        if (!throttled) {
            this._liveUndoPushed = false; // burst over
            this.regenerateAllSliceGroups();
            this.updateGroupControls();
            return;
        }
        const now = performance.now();
        if (now - (this._lastLiveRegen || 0) >= 180) {
            this._lastLiveRegen = now;
            this.regenerateAllSliceGroups();
        }
    }

    /**
     * Wire a numeric input for REAL-TIME editing:
     * - typing / stepper arrows update the model live ('input' event,
     *   throttled), with the final commit on 'change'
     * - the LABEL becomes a scrub target: click-drag horizontally to
     *   change the value quickly (ew-resize cursor, per-gesture undo)
     * @param {HTMLInputElement} input - with ._scrubLabel set
     * @param {Object} opts {apply, min, max, step, perPixel}
     */
    _wireLiveNumeric(input, { apply, min, max, step, perPixel }) {
        const clampSnap = (v) => {
            v = Math.min(max, Math.max(min, v));
            return Math.round(v / step) * step;
        };

        // Live while typing / stepping
        input.addEventListener('input', () => {
            const v = parseFloat(input.value);
            if (!Number.isFinite(v)) return;
            this._applyLive(apply, clampSnap(v), true);
        });
        // Final commit
        input.addEventListener('change', () => {
            const v = parseFloat(input.value);
            if (!Number.isFinite(v)) { this.updateGroupControls(); return; }
            this._applyLive(apply, clampSnap(v), false);
        });

        // Label scrub (drag horizontally)
        const label = input._scrubLabel;
        if (!label) return;
        label.style.cursor = 'ew-resize';
        label.addEventListener('pointerdown', (e) => {
            if (e.target === input || !this.selectedSliceGroup) return;
            e.preventDefault();
            const startX = e.clientX;
            const startV = parseFloat(input.value) || 0;
            try { label.setPointerCapture(e.pointerId); } catch (_) { /* synthetic */ }
            const onMove = (ev) => {
                const v = clampSnap(startV + (ev.clientX - startX) * perPixel);
                input.value = String(+v.toFixed(4));
                this._applyLive(apply, v, true);
            };
            const onUp = () => {
                window.removeEventListener('pointermove', onMove);
                window.removeEventListener('pointerup', onUp);
                const v = parseFloat(input.value);
                if (Number.isFinite(v)) this._applyLive(apply, clampSnap(v), false);
            };
            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp);
        });
    }

    /**
     * Create the compact one-row Start/End/Spacing controls for the
     * selected slice group. Hidden until a group is selected.
     */
    createGroupControlsRow() {
        const row = document.createElement('div');
        row.className = 'slice-group-controls';
        row.style.cssText = `
            display: none;
            align-items: center;
            gap: 6px;
            flex-wrap: wrap;
        `;

        const makeInput = (labelText, min, max, step, title) => {
            const label = document.createElement('label');
            label.title = title;
            label.style.cssText = `
                display: flex;
                align-items: center;
                gap: 2px;
                color: #666;
                font-size: 9px;
            `;
            label.textContent = labelText;

            const input = document.createElement('input');
            input.type = 'number';
            input.min = String(min);
            input.max = String(max);
            input.step = String(step);
            input.style.cssText = `
                width: 44px;
                font-size: 10px;
                padding: 1px 2px;
            `;
            // Don't let typing in the input trigger the app's key shortcuts
            input.addEventListener('keydown', (e) => e.stopPropagation());
            label.appendChild(input);
            row.appendChild(label);
            input._scrubLabel = label; // label doubles as a scrub target
            return input;
        };

        this.groupStartInput = makeInput('Start', 0, 1, 0.01, 'Group start position along the guide path (0..1). Drag the label to scrub.');
        this.groupEndInput = makeInput('End', 0, 1, 0.01, 'Group end position along the guide path (0..1). Drag the label to scrub.');
        this.groupSpacingInput = makeInput('Spacing', 15, 500, 1, 'Distance between slices (mm, min 15). Drag the label to scrub.');
        this.groupCapLenInput = makeInput('Cap len', 0, 200, 1, 'Endcap extension past the outer panels (mm). Drag the label to scrub.');
        this.groupCapRadiusInput = makeInput('Radius %', 0, 300, 1, 'Endcap corner radius as a percent of the classic radius (Rounded/Winged caps). Drag the label to scrub.');

        // REAL-TIME editing: input/scrub updates regenerate live (throttled),
        // final commit on change; labels are horizontal scrub targets
        this._wireLiveNumeric(this.groupStartInput, {
            apply: (sel, v) => { sel.start = Math.min(v, sel.end ?? 1); },
            min: 0, max: 1, step: 0.01, perPixel: 0.004
        });
        this._wireLiveNumeric(this.groupEndInput, {
            apply: (sel, v) => { sel.end = Math.max(v, sel.start ?? 0); },
            min: 0, max: 1, step: 0.01, perPixel: 0.004
        });
        this._wireLiveNumeric(this.groupSpacingInput, {
            apply: (sel, v) => { sel.spacing = v; },
            min: 15, max: 500, step: 1, perPixel: 1
        });
        this._wireLiveNumeric(this.groupCapLenInput, {
            apply: (sel, v) => { sel.capLength = v; },
            min: 0, max: 200, step: 1, perPixel: 0.5
        });
        this._wireLiveNumeric(this.groupCapRadiusInput, {
            apply: (sel, v) => { sel.capRadius = v; },
            min: 0, max: 300, step: 1, perPixel: 1
        });

        this.groupControlsRow = row;
        return row;
    }

    /**
     * Create the leg-brace-only controls row: a crossbrace toggle plus
     * WIDTH and BOTTOM OFFSET (mm) inputs. Shown only while a LEG-type
     * group ("Leg brace") is selected. The crossbrace is a horizontal
     * member between the two legs' lower ends, offset up from the feet,
     * unioned into the leg brace profile (one part).
     */
    createLegBraceControlsRow() {
        const row = document.createElement('div');
        row.className = 'leg-brace-controls';
        row.style.cssText = `
            display: none;
            align-items: center;
            gap: 6px;
            flex-wrap: wrap;
        `;

        // Crossbrace on/off
        const toggleLabel = document.createElement('label');
        toggleLabel.title = 'Add a crossbrace between the feet';
        toggleLabel.style.cssText = `
            display: flex;
            align-items: center;
            gap: 3px;
            color: #666;
            font-size: 9px;
            cursor: pointer;
        `;
        const toggle = document.createElement('input');
        toggle.type = 'checkbox';
        toggle.addEventListener('change', () => {
            const sel = this.selectedSliceGroup;
            if (!sel || sel.type !== SliceType.LEG) return;
            sel.crossbrace = toggle.checked;
            this.regenerateAllSliceGroups();
        });
        toggleLabel.appendChild(toggle);
        toggleLabel.appendChild(document.createTextNode('Crossbrace'));
        row.appendChild(toggleLabel);

        const makeNum = (labelText, min, max, title) => {
            const label = document.createElement('label');
            label.title = title;
            label.style.cssText = `
                display: flex;
                align-items: center;
                gap: 2px;
                color: #666;
                font-size: 9px;
            `;
            label.textContent = labelText;
            const input = document.createElement('input');
            input.type = 'number';
            input.min = String(min);
            input.max = String(max);
            input.step = '1';
            input.style.cssText = `
                width: 44px;
                font-size: 10px;
                padding: 1px 2px;
            `;
            input.addEventListener('keydown', (e) => e.stopPropagation());
            label.appendChild(input);
            row.appendChild(label);
            input._scrubLabel = label; // label doubles as a scrub target
            return input;
        };

        this.braceWidthInput = makeNum('Width', 5, 500, 'Crossbrace width (mm). Drag the label to scrub.');
        this.braceOffsetInput = makeNum('Offset', 0, 500, 'Crossbrace offset up from the feet (mm). Drag the label to scrub.');

        // Live editing + label scrub, same pattern as Start/End/Spacing
        this._wireLiveNumeric(this.braceWidthInput, {
            apply: (sel, v) => { sel.crossbraceWidth = v; },
            min: 5, max: 500, step: 1, perPixel: 1
        });
        this._wireLiveNumeric(this.braceOffsetInput, {
            apply: (sel, v) => { sel.crossbraceBottomOffset = v; },
            min: 0, max: 500, step: 1, perPixel: 1
        });

        this.braceToggleInput = toggle;
        this.legBraceControlsRow = row;
        return row;
    }

    /**
     * Show/refresh the Start/End/Spacing inputs for the selected group,
     * or hide the row when nothing is selected. LEG-type groups ("Leg
     * brace") show the crossbrace controls instead - start/end/spacing
     * have no meaning for a single rib tied to a leg.
     */
    updateGroupControls() {
        if (!this.groupControlsRow) return;

        const sel = this.selectedSliceGroup;
        const isLeg = !!sel && sel.type === SliceType.LEG;

        if (this.legBraceControlsRow) {
            this.legBraceControlsRow.style.display = isLeg ? 'flex' : 'none';
            if (isLeg) {
                this.braceToggleInput.checked = !!sel.crossbrace;
                this.braceWidthInput.value = String(Math.round(sel.crossbraceWidth ?? 40));
                this.braceOffsetInput.value = String(Math.round(sel.crossbraceBottomOffset ?? 20));
            }
        }

        if (!sel || isLeg) {
            this.groupControlsRow.style.display = 'none';
            return;
        }

        this.groupControlsRow.style.display = 'flex';
        if (document.activeElement !== this.groupStartInput) {
            this.groupStartInput.value = (sel.start ?? 0).toFixed(2);
        }
        if (document.activeElement !== this.groupEndInput) {
            this.groupEndInput.value = (sel.end ?? 1).toFixed(2);
        }
        if (document.activeElement !== this.groupSpacingInput) {
            this.groupSpacingInput.value = String(Math.round(sel.spacing ?? 50));
        }
        if (this.groupCapLenInput && document.activeElement !== this.groupCapLenInput) {
            // Default cap length = outer panel thickness + 10 (see
            // buildCrossSliceProfile.capLengthAt)
            const defaultLen = (this.app.materialThickness ?? 5) + 10;
            this.groupCapLenInput.value = String(Math.round(sel.capLength ?? defaultLen));
        }
        if (this.groupCapRadiusInput && document.activeElement !== this.groupCapRadiusInput) {
            this.groupCapRadiusInput.value = String(Math.round(sel.capRadius ?? 100));
        }

        // Keep the curved-caps toggle in sync with the selected group
        this.options.curvedCaps = sel.curvedCaps !== false;
        this.updateOptionStates();
    }

    /**
     * Update the slice groups list with current CrossSliceSelections
     */
    updateSliceGroupsList() {
        if (!this.sliceGroupsList) return;

        // Clear existing items
        this.sliceGroupsList.innerHTML = '';

        // Get all CrossSliceSelections from the application
        const selections = this.app.crossSliceSelections || [];

        console.log('🔄 Updating slice groups list...');
        console.log(`   - Total selections: ${selections.length}`);
        selections.forEach((sel, i) => {
            console.log(`   - Selection ${i}: type=${sel.type}, planes=${sel.planes?.length || 0}`);
        });

        if (selections.length === 0) {
            // Show empty state
            const emptyMsg = document.createElement('div');
            emptyMsg.textContent = 'No slices';
            emptyMsg.style.cssText = `
                color: #999;
                font-size: 11px;
                font-style: italic;
                padding: 4px 8px;
            `;
            this.sliceGroupsList.appendChild(emptyMsg);
            return;
        }

        // Create item for each selection
        selections.forEach((selection, index) => {
            const item = this.createSliceGroupItem(selection, index);
            this.sliceGroupsList.appendChild(item);
        });

        console.log(`Updated slice groups list: ${selections.length} groups`);
    }

    /**
     * Create a slice group list row: type icon + "Ribs (11 slices)" +
     * delete x. Click selects the group.
     * @param {CrossSliceSelection} selection - The slice selection
     * @param {number} index - Index in the list
     */
    createSliceGroupItem(selection, index) {
        const isSelected = this.selectedSliceGroup === selection;

        const row = document.createElement('div');
        row.className = 'props-row slice-group-item';
        if (isSelected) row.classList.add('selected');

        // Type icon
        const icon = document.createElement('img');
        icon.src = this.getIconForSliceType(selection.type);
        icon.alt = `Slice group ${index + 1}`;
        icon.style.cssText = `
            display: block;
            image-rendering: crisp-edges;
            width: 20px;
            height: 20px;
            object-fit: contain;
            flex-shrink: 0;
        `;
        row.appendChild(icon);

        // Label: "Ribs (11 slices)"
        const planeCount = selection.planes?.length || 0;
        const label = document.createElement('div');
        label.className = 'props-row-label';
        label.textContent =
            `${this.getLabelForSliceType(selection.type)} ` +
            `(${planeCount} slice${planeCount !== 1 ? 's' : ''})`;
        row.appendChild(label);

        // Delete button
        const editBtn = document.createElement('button');
        editBtn.className = 'props-row-x';
        editBtn.title = 'Edit - drag slices of this group in the viewport';
        editBtn.appendChild(phIcon(ICONS.pencilSimple, 12));
        editBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.selectSliceGroup(selection);
            this.startEditSlices();
        });
        row.appendChild(editBtn);

        const deleteBtn = document.createElement('button');
        deleteBtn.className = 'props-row-x';
        deleteBtn.title = 'Delete slice group';
        deleteBtn.appendChild(phIcon(ICONS.x, 12));
        deleteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.deleteSliceGroup(selection);
        });
        row.appendChild(deleteBtn);

        // Click to select
        row.addEventListener('click', () => {
            this.selectSliceGroup(selection);
        });

        return row;
    }

    /**
     * Get icon path for a slice type
     * @param {number} sliceType - SliceType enum value
     */
    getIconForSliceType(sliceType) {
        switch (sliceType) {
            case SliceType.SLICES:
                return '/images/gui/GUI_SLICE_MULTISLICE_ICON.png';
            case SliceType.SINGLE_SLICE:
                return '/images/gui/GUI_SLICE_SLICE_ICON.png';
            case SliceType.LEG:
                return '/images/gui/GUI_SLICE_LEG_ICON.png';
            case SliceType.SLATSLICES:
                return '/images/gui/GUI_SLICE_SLAT_BUTTON.png';
            case SliceType.PLANE_ON_EDGE:
                return '/images/gui/GUI_SLICE_SLICE_ICON.png';
            default:
                return '/images/gui/GUI_SLICE_MULTISLICE_ICON.png';
        }
    }

    /**
     * Get label for a slice type
     * @param {number} sliceType - SliceType enum value
     */
    getLabelForSliceType(sliceType) {
        switch (sliceType) {
            case SliceType.SLICES:
                return 'Ribs';
            case SliceType.SINGLE_SLICE:
                return 'Slice';
            case SliceType.LEG:
                return 'Leg brace';
            case SliceType.SLATSLICES:
                return 'Slats';
            case SliceType.PLANE_ON_EDGE:
                return 'Edge';
            default:
                return 'Group';
        }
    }

    /**
     * Select a slice group
     * @param {CrossSliceSelection} selection - The selection to select
     */
    selectSliceGroup(selection) {
        this.selectedSliceGroup = selection;
        this.editingSelection = selection;

        console.log('Selected slice group:', selection);
        console.log('  - Type:', selection.type);
        console.log('  - Planes:', selection.planes?.length || 0);
        console.log('  - Start:', selection.start);
        console.log('  - End:', selection.end);
        console.log('  - Spacing:', selection.spacing);

        // Update options panel to show this selection's settings
        this.options.smooth = selection.smooth || false;
        this.options.flushTops = selection.generateFlushTops || false;
        this.options.flipSlots = selection.flipSide || false;
        // Through All ON = do NOT crop to the generating shape
        this.options.throughAll = selection.cropToCurrentShape === false;
        this.activeCapStyle = selection.capType || CapType.CAP_CURVE;

        // Update UI to reflect selection (list highlight, controls,
        // cap/option active states, options-area visibility)
        this.update();
    }

    /**
     * Delete a slice group
     * Removes the selection from the app and regenerates - the group's
     * planes disappear because regeneration clears the manager first.
     * @param {CrossSliceSelection} selection - The selection to delete
     */
    deleteSliceGroup(selection) {
        const selections = this.app.crossSliceSelections || [];
        const index = selections.indexOf(selection);
        if (index >= 0) {
            selections.splice(index, 1);
        }

        // Leg brace groups are re-derived from the drawn legs on every
        // regeneration - remember the user removed this one so it stays
        // deleted (until the legs themselves change)
        if (selection.type === SliceType.LEG) {
            (this.app.disabledLegSlices ??= new Set()).add(selection.legIndex ?? 0);
        }

        if (this.editingSelection === selection) {
            this.editingSelection = null;
        }
        if (this.selectedSliceGroup === selection) {
            this.selectedSliceGroup = null;
        }

        console.log('Deleted slice group, regenerating remaining groups...');

        // Full regeneration through the app pipeline
        this.regenerateAllSliceGroups();
        this.update();
    }

    /**
     * Called by Application.setSliceTool when the interactive slice tool
     * is armed or disarmed - updates button active states.
     * @param {Object|null} sliceTool - {type: 'single'|'group'|'edit'} or null
     */
    onSliceToolChanged(sliceTool) {
        const toolNames = {
            single: 'single-slice',
            group: 'slice-group',
            edit: 'edit-slices'
        };
        this.activeTool = sliceTool ? (toolNames[sliceTool.type] || null) : null;
        this.updateSliceToolButtons();
    }

    /**
     * Reflect the armed slice tool on the panel buttons (and on the
     * section-header "+" button, which arms the group tool)
     */
    updateSliceToolButtons() {
        const armedType = this.app.sliceTool?.type || null;
        const typeByLabel = { 'Slice': 'single', 'Edit': 'edit' };

        for (const [lbl, ref] of Object.entries(this.sliceToolButtons)) {
            const active = armedType !== null && armedType === typeByLabel[lbl];
            ref.button.dataset.active = active ? 'true' : 'false';
            ref.button.classList.toggle('active', active);
        }

        if (this.headerAddButton) {
            this.headerAddButton.classList.toggle('active', armedType === 'group');
        }
    }

    /**
     * Reflect the current cap style and option toggles on their buttons
     */
    updateOptionStates() {
        if (this.capButtons) {
            for (const { button, capType } of this.capButtons) {
                button.classList.toggle('active', this.activeCapStyle === capType);
            }
        }
        if (this.optionToggleButtons) {
            for (const { button, key } of this.optionToggleButtons) {
                button.classList.toggle('active', !!this.options[key]);
            }
        }
    }

    /**
     * Show the options area only while a group is selected; otherwise show
     * the dim hint under the list
     */
    updateOptionsVisibility() {
        const hasSelection = !!this.selectedSliceGroup;
        if (this.optionsArea) {
            this.optionsArea.style.display = hasSelection ? 'flex' : 'none';
        }
        if (this.hintEl) {
            this.hintEl.style.display = hasSelection ? 'none' : 'block';
        }
    }

    /**
     * Resolve which sketch shape should guide a new slice group.
     * Preference: currently selected spline > first non-leg spline >
     * first closed shape. Returns -1 if the sketch is truly empty.
     * The actual guide path object is re-resolved by the app pipeline on
     * every regeneration (shapes are cloned on every edit).
     * @returns {number} shapeIndex, or -1 if no suitable shape exists
     */
    resolveGuideShapeIndex() {
        const sketchPlane = this.app.sketchPlane;
        if (!sketchPlane) return -1;

        const splines = sketchPlane.splines || [];
        const closedShapes = sketchPlane.closedShapes || [];

        // 1. Currently selected spline
        if (sketchPlane.selectedSpline) {
            const idx = splines.indexOf(sketchPlane.selectedSpline);
            if (idx >= 0) return idx;
        }

        // 2. First non-leg spline
        for (let i = 0; i < splines.length; i++) {
            if (splines[i].type !== SketchSpline.TYPE_LEG) {
                return i;
            }
        }

        // 3. First closed shape
        if (closedShapes.length > 0) return 0;

        return -1;
    }

    /**
     * Create a slice group selection and register it with the app.
     * The app pipeline resolves the guide path/plane and generates planes.
     * @param {number} type - SliceType for the new group
     * @param {Object} params - start/end/spacing overrides
     * @returns {CrossSliceSelection|null} The created selection
     */
    createSliceSelection(type, params = {}) {
        // Interactive placement supplies the clicked shape's index; the
        // panel-button path falls back to the heuristic resolution
        const shapeIndex = (params.shapeIndex != null)
            ? params.shapeIndex
            : this.resolveGuideShapeIndex();
        if (shapeIndex < 0) {
            console.warn('⚠️ Sketch is empty - draw a shape first!');
            console.warn('Please draw a shape first before creating slices');
            return null;
        }

        const selection = new CrossSliceSelection({
            path: null,   // Resolved from the current sketch on regeneration
            plane: null,  // Assigned by Application.autoGenerateCrossSlices
            start: params.start ?? 0,
            end: params.end ?? 1,
            spacing: params.spacing ?? 50,
            type: type
        });

        // Apply current cap style and options
        selection.capType = this.activeCapStyle;
        selection.smooth = this.options.smooth;
        selection.generateFlushTops = this.options.flushTops;
        selection.flipSide = this.options.flipSlots;
        // Through All ON = do NOT crop to the generating shape
        selection.cropToCurrentShape = !this.options.throughAll;
        selection.shapeIndex = shapeIndex;

        // Register with the app so regeneration picks it up
        this.app.crossSliceSelections.push(selection);

        // Select it for editing
        this.editingSelection = selection;
        this.selectedSliceGroup = selection;

        console.log(`   - Created slice selection (type ${type}, shape ${shapeIndex})`);

        // Full regeneration through the app pipeline (resolves guide path,
        // sets selection.plane, generates planes and meshes, updates panel)
        this.regenerateAllSliceGroups();
        this.update();

        return selection;
    }

    /**
     * Start single slice workflow (interactive).
     * Arms the slice tool: the next click on a sketch shape places a
     * single cross-slice at the clicked point. Clicking the button again
     * (or Escape) disarms.
     */
    startSingleSlice() {
        if (!this.app.slicePlaneManager) {
            console.warn('SlicePlaneManager not initialized');
            return;
        }

        if (this.app.sliceTool?.type === 'single') {
            this.app.setSliceTool(null); // Toggle off
            return;
        }

        console.log('🔪 Single slice tool armed - click a shape to place the slice');
        this.app.setSliceTool('single');
    }

    /**
     * Start slice group workflow (interactive).
     * Arms the slice tool: the first click on a shape anchors the group's
     * start, a second click on the SAME shape sets the end and creates the
     * group. Clicking the button again (or Escape) disarms.
     */
    startSliceGroup() {
        if (!this.app.slicePlaneManager) {
            console.warn('SlicePlaneManager not initialized');
            return;
        }

        if (this.app.sliceTool?.type === 'group') {
            this.app.setSliceTool(null); // Toggle off
            return;
        }

        console.log('🔪🔪🔪 Slice group tool armed - click start and end points on a shape');
        this.app.setSliceTool('group');
    }

    /**
     * Start slice edit workflow (interactive).
     * Arms the edit tool: hovering highlights the rib chord under the
     * cursor, click-drag grabs and repositions it (single slices follow the
     * cursor; a group's end ribs adjust its start/end; middle ribs translate
     * the whole group). Grabbing a rib also selects its group in the panel.
     * Clicking the button again (or Escape) disarms.
     */
    startEditSlices() {
        if (this.app.sliceTool?.type === 'edit') {
            this.app.setSliceTool(null); // Toggle off
            return;
        }

        console.log('✋ Slice edit tool armed - click-drag a rib to reposition it');
        this.app.setSliceTool('edit');
    }

    /**
     * Start single slat workflow
     */
    startSingleSlat() {
        if (!this.app.slicePlaneManager) {
            console.warn('SlicePlaneManager not initialized');
            return;
        }

        console.log('Starting single slat workflow');
        this.activeTool = 'single-slat';

        // Similar to single slice but generates horizontal supports
        // TODO: Implement slat workflow (requires guide path for height)
        console.log('Single slat workflow not yet fully implemented');
    }

    /**
     * Start slat group workflow
     * Creates horizontal slat supports with finger joints
     */
    startSlatGroup() {
        if (!this.app.slicePlaneManager) {
            console.warn('SlicePlaneManager not initialized');
            return;
        }

        console.log('🪚 Starting slat group workflow');
        this.activeTool = 'slat-group';

        const selection = this.createSliceSelection(SliceType.SLATSLICES, {
            start: 0.0,
            end: 1.0,
            spacing: 50
        });

        if (selection) {
            selection.teethCount = 5;  // Finger joint teeth
            selection.slatHeight = 30; // Slat height in mm
        }
    }

    /**
     * Set cap style
     * @param {number} capType - Cap type from CapType enum
     */
    setCapStyle(capType) {
        this.activeCapStyle = capType;
        console.log(`Cap style set to: ${capType}`);

        // If we have an active selection, update it
        if (this.editingSelection) {
            this.editingSelection.capType = capType;
            // Regenerate with new cap style
            this.regenerateActiveSelection();
        }

        this.updateOptionStates();
    }

    /**
     * Toggle an option
     * @param {string} optionName - Option name
     */
    toggleOption(optionName) {
        this.options[optionName] = !this.options[optionName];
        console.log(`Option ${optionName}: ${this.options[optionName]}`);

        // Apply to the selected slice group, falling back to the
        // auto-generated rib group so the toggles always act on something
        const target = this.editingSelection ||
            (this.app.crossSliceSelections && this.app.crossSliceSelections[0]) || null;

        if (target) {
            if (optionName === 'smooth') {
                target.smooth = this.options.smooth;
            } else if (optionName === 'flushTops') {
                target.generateFlushTops = this.options.flushTops;
            } else if (optionName === 'flipSlots') {
                target.flipSide = this.options.flipSlots;
            } else if (optionName === 'throughAll') {
                // Through All ON = do NOT crop to the generating shape
                target.cropToCurrentShape = !this.options.throughAll;
            } else if (optionName === 'curvedCaps') {
                target.curvedCaps = this.options.curvedCaps;
            }

            // Regenerate through the full app pipeline so the change
            // takes effect visually (planes + meshes)
            this.regenerateActiveSelection();
        }

        this.updateOptionStates();
    }

    /**
     * Regenerate slice groups after editing a selection.
     * Routes through the Application pipeline (NOT the manager directly):
     * autoCreatePrimarySlicePlane refreshes the sketch snapshots and calls
     * autoGenerateCrossSlices, which clears the manager, regenerates every
     * slice group along its guide path, and rebuilds all meshes.
     */
    regenerateActiveSelection() {
        this.regenerateAllSliceGroups();
    }

    /**
     * Trigger a full slice/mesh regeneration through the app pipeline
     */
    regenerateAllSliceGroups() {
        if (!this.app || typeof this.app.regenerateAll !== 'function') {
            console.warn('Application pipeline not available for slice regeneration');
            return;
        }
        // Canonical pipeline (slices, slots, leg braces, meshes, physics)
        this.app.regenerateAll();
    }

    /**
     * Finish editing current selection
     */
    finishEditing() {
        if (this.editingSelection) {
            this.editingSelection.stopEditing();

            // Run complete generation workflow
            this.app.slicePlaneManager.generateComplete({
                capType: this.activeCapStyle,
                smooth: this.options.smooth,
                flushTops: this.options.flushTops
            });

            this.editingSelection = null;
            this.activeTool = null;

            console.log('Finished editing, generated complete model');
        }
    }

    /**
     * Get the panel element
     */
    getElement() {
        return this.panel;
    }

    /**
     * Update panel state
     * Refreshes the slice groups list, controls and active states
     */
    update() {
        // Update the slice groups list to reflect current CrossSliceSelections
        this.updateSliceGroupsList();

        // Refresh the selected group's Start/End/Spacing inputs
        this.updateGroupControls();

        // Reflect the armed interactive slice tool on the buttons
        this.updateSliceToolButtons();

        // Reflect cap style + option toggles
        this.updateOptionStates();

        // Options only while a group is selected
        this.updateOptionsVisibility();
    }
}
